import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  BarChart3, Users, UserCheck, Clock, TrendingUp, BookOpen,
  Award, FileText, AlertTriangle, RefreshCcw, Download, Calendar,
  GraduationCap, CheckCircle, XCircle, AlertCircle, FileSpreadsheet
} from 'lucide-react';

const PedagogicalReportPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [filters, setFilters] = useState({
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    group_id: '',
    course_id: '',
    trainer_id: '',
    student_id: ''
  });
  const [filterOptions, setFilterOptions] = useState({
    groups: [],
    courses: [],
    trainers: [],
    students: []
  });
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchFilterOptions();
    fetchReport();
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/pedagogical-report/filters');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  // Reject an inverted period (start after end) before any request
  const isDateRangeValid = () => {
    if (filters.start_date && filters.end_date && filters.end_date < filters.start_date) {
      toast.error('La date de début doit être inférieure ou égale à la date de fin.');
      return false;
    }
    return true;
  };

  const fetchReport = async () => {
    if (!isDateRangeValid()) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.group_id) params.append('group_id', filters.group_id);
      if (filters.course_id) params.append('course_id', filters.course_id);
      if (filters.trainer_id) params.append('trainer_id', filters.trainer_id);
      if (filters.student_id) params.append('student_id', filters.student_id);

      const response = await api.get(`/pedagogical-report/data?${params.toString()}`);
      setReportData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du chargement du bilan');
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    if (!isDateRangeValid()) return;
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.group_id) params.append('group_id', filters.group_id);
      if (filters.course_id) params.append('course_id', filters.course_id);
      if (filters.trainer_id) params.append('trainer_id', filters.trainer_id);
      if (filters.student_id) params.append('student_id', filters.student_id);

      const response = await api.get(`/pedagogical-report/export/pdf?${params.toString()}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bilan_pedagogique_${filters.start_date}_${filters.end_date}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('PDF exporté avec succès');
    } catch (error) {
      toast.error('Erreur lors de l\'export PDF');
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = async () => {
    if (!isDateRangeValid()) return;
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.group_id) params.append('group_id', filters.group_id);
      if (filters.course_id) params.append('course_id', filters.course_id);
      if (filters.trainer_id) params.append('trainer_id', filters.trainer_id);
      if (filters.student_id) params.append('student_id', filters.student_id);

      const response = await api.get(`/pedagogical-report/export/excel?${params.toString()}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bilan_pedagogique_${filters.start_date}_${filters.end_date}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Excel exporté avec succès');
    } catch (error) {
      toast.error('Erreur lors de l\'export Excel');
    } finally {
      setExporting(false);
    }
  };

  const getRiskBadge = (level) => {
    switch (level) {
      case 'élevé':
        return <Badge className="bg-red-500 text-white">ÉLEVÉ</Badge>;
      case 'moyen':
        return <Badge className="bg-amber-500 text-white">MOYEN</Badge>;
      default:
        return <Badge className="bg-emerald-500 text-white">FAIBLE</Badge>;
    }
  };

  const KPICard = ({ icon: Icon, title, value, suffix = '', color = 'violet' }) => {
    const colorClasses = {
      violet: 'bg-violet-50 dark:bg-violet-900/20 text-violet-600',
      blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600',
      emerald: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600',
      amber: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600',
      red: 'bg-red-50 dark:bg-red-900/20 text-red-600',
      slate: 'bg-slate-50 dark:bg-slate-900/20 text-slate-600'
    };

    return (
      <div className={`p-4 rounded-xl ${colorClasses[color]}`}>
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5" />
          <div>
            <p className="text-xs opacity-80">{title}</p>
            <p className="text-xl font-bold">{value}{suffix}</p>
          </div>
        </div>
      </div>
    );
  };

  if (loading && !reportData) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCcw className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  const kpis = reportData?.kpis || {};

  return (
    <div className="space-y-6" data-testid="pedagogical-report-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-violet-600" />
            Bilan Pédagogique
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            Analyse complète de l'activité pédagogique
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleExportPDF}
            disabled={exporting}
            data-testid="export-pdf-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
          <Button
            variant="outline"
            onClick={handleExportExcel}
            disabled={exporting}
            data-testid="export-excel-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card data-testid="filters-section">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Filtres
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <div className="space-y-1">
              <Label className="text-xs">Date début</Label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                data-testid="filter-start-date"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Date fin</Label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                data-testid="filter-end-date"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Groupe</Label>
              <Select
                value={filters.group_id}
                onValueChange={(value) => setFilters({ ...filters, group_id: value === 'all' ? '' : value })}
              >
                <SelectTrigger data-testid="filter-group">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les groupes</SelectItem>
                  {filterOptions.groups.map((g) => (
                    <SelectItem key={g.id} value={g.id.toString()}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Cours</Label>
              <Select
                value={filters.course_id}
                onValueChange={(value) => setFilters({ ...filters, course_id: value === 'all' ? '' : value })}
              >
                <SelectTrigger data-testid="filter-course">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les cours</SelectItem>
                  {filterOptions.courses.map((c) => (
                    <SelectItem key={c.id} value={c.id.toString()}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Formateur</Label>
              <Select
                value={filters.trainer_id}
                onValueChange={(value) => setFilters({ ...filters, trainer_id: value === 'all' ? '' : value })}
              >
                <SelectTrigger data-testid="filter-trainer">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les formateurs</SelectItem>
                  {filterOptions.trainers.map((t) => (
                    <SelectItem key={t.id} value={t.id.toString()}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Étudiant</Label>
              <Select
                value={filters.student_id}
                onValueChange={(value) => setFilters({ ...filters, student_id: value === 'all' ? '' : value })}
              >
                <SelectTrigger data-testid="filter-student">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les étudiants</SelectItem>
                  {filterOptions.students.map((s) => (
                    <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={fetchReport} className="w-full bg-violet-600 hover:bg-violet-700" data-testid="refresh-btn">
                <RefreshCcw className="w-4 h-4 mr-2" />
                Actualiser
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPIs Grid - 15 indicators */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="kpi-grid">
        <KPICard icon={Users} title="Total apprenants" value={kpis.total_students || 0} color="violet" />
        <KPICard icon={UserCheck} title="Apprenants actifs" value={kpis.active_students || 0} color="blue" />
        <KPICard icon={CheckCircle} title="Taux de présence" value={kpis.attendance_rate || 0} suffix="%" color="emerald" />
        <KPICard icon={XCircle} title="Taux d'absentéisme" value={kpis.absence_rate || 0} suffix="%" color="red" />
        <KPICard icon={Clock} title="Taux de retard" value={kpis.late_rate || 0} suffix="%" color="amber" />
        <KPICard icon={TrendingUp} title="Progression moyenne" value={kpis.average_progression || 0} suffix="%" color="blue" />
        <KPICard icon={BookOpen} title="Taux de complétion" value={kpis.completion_rate || 0} suffix="%" color="emerald" />
        <KPICard icon={Award} title="Note moyenne" value={kpis.average_grade || 0} suffix="/20" color="violet" />
        <KPICard icon={CheckCircle} title="Taux de réussite" value={kpis.success_rate || 0} suffix="%" color="emerald" />
        <KPICard icon={FileText} title="Rendu devoirs" value={kpis.homework_submission_rate || 0} suffix="%" color="blue" />
        <KPICard icon={AlertTriangle} title="Apprenants à risque" value={kpis.at_risk_students || 0} color="red" />
        <KPICard icon={CheckCircle} title="Émargements validés" value={kpis.validated_attendances || 0} color="emerald" />
        <KPICard icon={FileText} title="Documents générés" value={kpis.generated_documents || 0} color="slate" />
        <KPICard icon={AlertCircle} title="Tickets ouverts" value={kpis.open_tickets || 0} color="amber" />
        <KPICard icon={GraduationCap} title="Groupes suivis" value={kpis.covered_groups || 0} color="violet" />
      </div>

      {/* Detailed Tables */}
      <Tabs defaultValue="students" className="space-y-4">
        <TabsList className="grid grid-cols-4 w-full max-w-2xl">
          <TabsTrigger value="students" data-testid="tab-students">Étudiants</TabsTrigger>
          <TabsTrigger value="groups" data-testid="tab-groups">Groupes</TabsTrigger>
          <TabsTrigger value="courses" data-testid="tab-courses">Cours</TabsTrigger>
          <TabsTrigger value="trainers" data-testid="tab-trainers">Formateurs</TabsTrigger>
        </TabsList>

        {/* Students Tab */}
        <TabsContent value="students">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-violet-600" />
                Détail par Étudiant
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table data-testid="students-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nom / Prénom</TableHead>
                      <TableHead>Groupe</TableHead>
                      <TableHead>Formateur</TableHead>
                      <TableHead className="text-center">Présence</TableHead>
                      <TableHead className="text-center">Progression</TableHead>
                      <TableHead className="text-center">Complétion</TableHead>
                      <TableHead className="text-center">Note Moy.</TableHead>
                      <TableHead className="text-center">Devoirs</TableHead>
                      <TableHead className="text-center">Risque</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData?.students?.map((student) => (
                      <TableRow key={student.id}>
                        <TableCell className="font-medium">
                          {student.last_name} {student.first_name}
                        </TableCell>
                        <TableCell>{student.group_name || '-'}</TableCell>
                        <TableCell>{student.trainer_name || '-'}</TableCell>
                        <TableCell className="text-center">{student.attendance_rate}%</TableCell>
                        <TableCell className="text-center">{student.progression}%</TableCell>
                        <TableCell className="text-center">{student.completion_rate}%</TableCell>
                        <TableCell className="text-center">{student.average_grade}/20</TableCell>
                        <TableCell className="text-center">{student.homework_rate}%</TableCell>
                        <TableCell className="text-center">{getRiskBadge(student.risk_level)}</TableCell>
                      </TableRow>
                    ))}
                    {(!reportData?.students || reportData.students.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center text-slate-500 py-8">
                          Aucun étudiant trouvé pour cette période
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Groups Tab */}
        <TabsContent value="groups">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-emerald-600" />
                Synthèse par Groupe
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table data-testid="groups-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Groupe</TableHead>
                    <TableHead className="text-center">Nb Étudiants</TableHead>
                    <TableHead className="text-center">Présence</TableHead>
                    <TableHead className="text-center">Progression</TableHead>
                    <TableHead className="text-center">Note Moyenne</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportData?.groups?.map((group) => (
                    <TableRow key={group.id}>
                      <TableCell className="font-medium">{group.name}</TableCell>
                      <TableCell className="text-center">{group.student_count}</TableCell>
                      <TableCell className="text-center">{group.attendance_rate}%</TableCell>
                      <TableCell className="text-center">{group.average_progression}%</TableCell>
                      <TableCell className="text-center">{group.average_grade}/20</TableCell>
                    </TableRow>
                  ))}
                  {(!reportData?.groups || reportData.groups.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                        Aucun groupe trouvé
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Courses Tab */}
        <TabsContent value="courses">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-blue-600" />
                Synthèse par Cours
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table data-testid="courses-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Cours</TableHead>
                    <TableHead className="text-center">Nb Étudiants</TableHead>
                    <TableHead className="text-center">Nb Formateurs</TableHead>
                    <TableHead className="text-center">Complétion</TableHead>
                    <TableHead className="text-center">Note Moyenne</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportData?.courses?.map((course) => (
                    <TableRow key={course.id}>
                      <TableCell className="font-medium">{course.name}</TableCell>
                      <TableCell className="text-center">{course.student_count}</TableCell>
                      <TableCell className="text-center">{course.trainer_count}</TableCell>
                      <TableCell className="text-center">{course.completion_rate}%</TableCell>
                      <TableCell className="text-center">{course.average_grade}/20</TableCell>
                    </TableRow>
                  ))}
                  {(!reportData?.courses || reportData.courses.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                        Aucun cours trouvé
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trainers Tab */}
        <TabsContent value="trainers">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-amber-600" />
                Synthèse par Formateur
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table data-testid="trainers-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Formateur</TableHead>
                    <TableHead className="text-center">Nb Cours</TableHead>
                    <TableHead className="text-center">Nb Groupes</TableHead>
                    <TableHead className="text-center">Nb Étudiants</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportData?.trainers?.map((trainer) => (
                    <TableRow key={trainer.id}>
                      <TableCell className="font-medium">
                        {trainer.last_name} {trainer.first_name}
                      </TableCell>
                      <TableCell className="text-center">{trainer.course_count}</TableCell>
                      <TableCell className="text-center">{trainer.group_count}</TableCell>
                      <TableCell className="text-center">{trainer.student_count}</TableCell>
                    </TableRow>
                  ))}
                  {(!reportData?.trainers || reportData.trainers.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-slate-500 py-8">
                        Aucun formateur trouvé
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Generated info */}
      {reportData?.generated_at && (
        <p className="text-xs text-slate-400 text-center">
          Bilan généré le {new Date(reportData.generated_at).toLocaleString('fr-FR')}
        </p>
      )}
    </div>
  );
};

export default PedagogicalReportPage;
